/**
 * OrgInterviewsPage - 조직원 면담 페이지
 *
 * 조직원 면담 조회 및 관리 기능 제공
 * 실장 단위까지만 접근 가능 (본부장/대표 접근 불가)
 *
 * 플로우:
 * - 탭1(면담진행): 면담 정보 입력 → "면담 기록 작성" 버튼 → InterviewWritePage로 이동
 * - 탭2(면담기록): 면담 목록 표시 → 면담 클릭 → InterviewWritePage로 이동
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, AlertCircle, RefreshCw, X, FileText, ClipboardList, Loader, Settings } from 'lucide-react';
import { useOrgManagementScope } from '../../shared/hooks/useOrgManagementScope';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { useJobTitles } from '../../shared/hooks/useJobTitles';
import { usePositionLevelMap } from '../../shared/hooks/usePositionLevelMap';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { getTimezoneByCompanyCode } from '../../shared/utils/dateUtils';  // V62: 면담일시 기본값 시차 적용
import {
  useInterviews,
  Interview,
  DELIVERY_STATUS_LABELS,
} from '../../shared/hooks/useInterviews';
import { useInterviewTypes } from '../../shared/hooks/useInterviewTypesManagement';
import { Employee } from '../../shared/types/employee';
import {
  compareOrgMember,
  buildJobTitleMaps,
  buildPositionLevelMaps,
  OrgMemberSortMaps,
} from '../../shared/utils/sortOrgMembers';
import {
  useEmployeeInterviewStatus,
  EmployeeInterviewStatus,
  INTERVIEW_STATUS_STYLES as CALCULATED_STATUS_STYLES,
  DeliveryStatus,
} from '../../shared/hooks/useInterviewCycleSettings';
import { InterviewCycleSettingsModal } from './components/InterviewCycleSettingsModal';
import { ModalOverlay } from '../../shared/components/ModalOverlay';

/**
 * 스코프 모드 → 한글 라벨
 */
const SCOPE_MODE_LABELS = {
  SELF: '본인 조직만',
  SUBTREE: '하위 조직 포함',
  ALL: '전체 조직',
};

/**
 * 면담관리 상태 타입 (레거시 - 현재 미사용)
 * V55.61부터 서버 계산 결과(CALCULATED_STATUS_STYLES) 사용
 */
type InterviewStatus = '미진행' | '진행중' | '완료';

/**
 * 면담관리 상태 라벨 및 스타일 (레거시 - 현재 미사용)
 * V55.61부터 useInterviewCycleSettings.INTERVIEW_STATUS_STYLES 사용
 */
const INTERVIEW_STATUS_STYLES: Record<InterviewStatus, { label: string; color: string; bg: string }> = {
  '미진행': { label: '미진행', color: 'var(--color-gray-600)', bg: 'var(--color-gray-100)' },
  '진행중': { label: '진행중', color: 'var(--color-blue-700)', bg: 'var(--color-blue-50)' },
  '완료': { label: '완료', color: 'var(--color-green-700)', bg: 'var(--color-green-50)' },
};

/**
 * 마지막 면담상태(deliveryStatus) 스타일 매핑
 * UI 표시용 색상 상수 (BE 도메인 값과 동기화 필요)
 *
 * - notSent(미전송): 면담이 대상자에게 전송되지 않음
 * - sent(미확인): 전송됨, 대상자 확인 대기 중
 * - agreed(상호합의): 대상자가 동의함
 * - disagreed(미동의): 대상자가 동의하지 않음
 *
 * 라벨: DELIVERY_STATUS_LABELS (useInterviews.ts)
 */
const DELIVERY_STATUS_COLORS: Record<DeliveryStatus, { color: string; bg: string }> = {
  notSent: { color: 'var(--color-gray-500)', bg: 'var(--color-gray-100)' },
  sent: { color: 'var(--color-blue-600)', bg: 'var(--color-blue-50)' },
  agreed: { color: 'var(--color-green-600)', bg: 'var(--color-green-50)' },
  disagreed: { color: 'var(--color-red-600)', bg: 'var(--color-red-50)' },
};

/**
 * 면담타입 (BE interviewTypes 컬렉션의 code 값)
 */
type InterviewType = string;

/**
 * 패널 탭 타입
 */
type PanelTab = 'interview' | 'history';

/**
 * V62: 페이지 탭 — 면담 / 수습면담
 */
type PageTab = 'general' | 'probation';

export function OrgInterviewsPage() {
  // ============================================
  // 스코프 조회
  // ============================================
  const {
    isLoading: scopeLoading,
    error: scopeError,
    scopeOrgUnitIds,
    scopeMode,
    requester,
    refetch: refetchScope,
  } = useOrgManagementScope();

  //스코프가 준비되었는지 확인
  const isScopeReady = !scopeLoading && scopeOrgUnitIds.length > 0;

  // ============================================
  // 직원 목록 조회
  // ============================================
  const {
    employees,
    isLoading: employeesLoading,
    error: employeesError,
  } = useEmployees(
    isScopeReady
      ? {
          orgUnitIds: scopeOrgUnitIds,
          scopeMode: scopeMode || undefined,
          status: 'active_or_on_leave', // 재직 + 휴직 (퇴사자 제외)
        }
      : {} // 스코프 미준비 시 빈 필터 → useEmployees 내부에서 호출 스킵
  );

  const { user } = useAuthorization();

  // ============================================
  // 정렬용 마스터 데이터 조회
  // ============================================
  const { jobTitles } = useJobTitles({ status: 'active' });
  const { positionLevelMap } = usePositionLevelMap();

  // 검색어 상태
  const [searchText, setSearchText] = useState('');

  // V55.61: 면담주기 설정 모달 상태
  const [showCycleSettingsModal, setShowCycleSettingsModal] = useState(false);

  // ============================================
  // 패널 상태
  // ============================================
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [activeTab, setActiveTab] = useState<PanelTab>('interview');

  // V62: 페이지 탭 (면담 / 수습면담)
  const [pageTab, setPageTab] = useState<PageTab>('general');

  // 면담진행 폼 상태
  // interviewDateTime → interviewDate + interviewTime 분리
  const [interviewDate, setInterviewDate] = useState(''); // YYYY-MM-DD
  const [interviewTime, setInterviewTime] = useState(''); // HH:mm
  const [interviewLocation, setInterviewLocation] = useState('');
  const [interviewType, setInterviewType] = useState<InterviewType>('regular');

  // ============================================
  // 패널 열기/닫기 핸들러
  // ============================================
  const handleOpenPanel = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setShowDetailPanel(true);
    setActiveTab('interview');

    // V62: 면담일시 기본값 — 면담 대상 직원의 companyCode 기준 시차 적용
    // 정책: 면담 데이터는 대상 직원 소속 회사 기준. 브라우저 TZ 의존 금지.
    const targetCompanyCode = employee.companyCode || user?.profile?.companyCode;
    const tz = getTimezoneByCompanyCode(targetCompanyCode);
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(now);
    const p = (type: string) => parts.find(p => p.type === type)?.value || '00';
    setInterviewDate(`${p('year')}-${p('month')}-${p('day')}`); // YYYY-MM-DD
    setInterviewTime(`${p('hour')}:${p('minute')}`);             // HH:mm

    // 기타 필드 초기화
    setInterviewLocation('');
    setInterviewType('regular');
  }, []);

  const handleClosePanel = useCallback(() => {
    setShowDetailPanel(false);
    setSelectedEmployee(null);
  }, []);

  // ESC 키로 패널 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showDetailPanel) {
        handleClosePanel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDetailPanel, handleClosePanel]);

  // "면담 기록" 버튼 클릭 → 탭2로 이동
  const handleGoToHistory = useCallback(() => {
    setActiveTab('history');
  }, []);

  // 면담 기록 작성 페이지로 이동
  // 면담 장소 필수 검증 추가
  const navigate = useNavigate();
  const handleGoToWritePage = useCallback(() => {
    if (!selectedEmployee) return;

    // 면담 장소 필수 검증
    if (!interviewLocation.trim()) {
      alert('면담 장소를 입력해주세요.');
      return;
    }

    // query params로 면담 정보 전달
    const params = new URLSearchParams({
      date: interviewDate,
      time: interviewTime,
      location: interviewLocation,
      type: interviewType,
    });

    navigate(`/org-management/interviews/${selectedEmployee.id}/new?${params.toString()}`);
  }, [selectedEmployee, interviewDate, interviewTime, interviewLocation, interviewType, navigate]);

  // ============================================
  // 공용 정렬 맵 생성
  // ============================================
  const sortMaps: OrgMemberSortMaps = useMemo(() => {
    const jobTitleMaps = buildJobTitleMaps(jobTitles);
    const positionLevelMaps = buildPositionLevelMaps(
      Object.entries(positionLevelMap).map(([id, pl]) => ({
        id,
        name: pl.name,
        rankWeight: pl.rankWeight,
      }))
    );
    return { ...jobTitleMaps, ...positionLevelMaps };
  }, [jobTitles, positionLevelMap]);

  // ============================================
  // 직원 필터링 및 정렬
  // 1. 본인 제외
  // 2. 검색어 필터
  // 3. 정렬: 직책→직위→입사일→이름
  // ============================================
  const filteredAndSortedEmployees = useMemo(() => {
    // 본인 ID (UserWithRoles.profile.id 또는 requester.uid)
    const myUserId = user?.profile?.id || requester?.uid;

    // 1. 본인 제외
    let filtered = employees.filter((emp) => emp.id !== myUserId);

    // 2. 검색어 필터
    if (searchText.trim()) {
      const search = searchText.toLowerCase().trim();
      filtered = filtered.filter((emp) => {
        const name = (emp.nameKo || '').toLowerCase();
        const orgName = (emp.orgUnitName || '').toLowerCase();
        const position = (emp.positionTitle || '').toLowerCase();
        const jobTitle = (emp.jobTitle || '').toLowerCase();
        return (
          name.includes(search) ||
          orgName.includes(search) ||
          position.includes(search) ||
          jobTitle.includes(search)
        );
      });
    }

    // 3. 정렬: 직책→직위→입사일→이름
    const sorted = [...filtered].sort((a, b) => compareOrgMember(a, b, sortMaps));

    return sorted;
  }, [employees, user, requester, searchText, sortMaps]);

  // ============================================
  // V55.61: 직원별 면담 상태 조회
  // ============================================
  const employeeIds = useMemo(() => {
    return filteredAndSortedEmployees.map((emp) => emp.id);
  }, [filteredAndSortedEmployees]);

  const {
    statuses: interviewStatuses,
    isLoading: statusLoading,
    refetch: refetchStatuses,
  } = useEmployeeInterviewStatus(employeeIds);

  // ============================================
  // 면담 임박일 기준 재정렬 (statuses 로드 후)
  // daysRemaining 오름차순 → 이름 가나다순, null은 맨 아래
  // ============================================
  const sortedByInterview = useMemo(() => {
    if (interviewStatuses.size === 0) return filteredAndSortedEmployees;
    return [...filteredAndSortedEmployees].sort((a, b) => {
      const sa = interviewStatuses.get(a.id);
      const sb = interviewStatuses.get(b.id);
      const da = sa?.status?.daysRemaining;
      const db = sb?.status?.daysRemaining;
      const hasA = da != null;
      const hasB = db != null;
      if (hasA !== hasB) return hasA ? -1 : 1;
      if (hasA && hasB && da !== db) return da - db;
      return (a.nameKo || '').localeCompare(b.nameKo || '', 'ko');
    });
  }, [filteredAndSortedEmployees, interviewStatuses]);

  // ============================================
  // V62: 탭별 직원 분류 — 수습면담 상호합의 기반
  // ============================================
  //
  // ── 수습기간 내 + 상호합의 미완료 → 2탭(수습면담) ──
  //   probationEndDate >= today && !hasProbationAgreement
  //
  // ── 그 외 → 1탭(면담) ──
  //   상호합의 완료 or 수습기간 만료 or 비수습 직원

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const { generalEmployees, probationTabEmployees } = useMemo(() => {
    const general: Employee[] = [];
    const probation: Employee[] = [];
    for (const emp of sortedByInterview) {
      const isInProbationPeriod = !!emp.probationEndDate && emp.probationEndDate >= today;
      const hasProbationAgreement = interviewStatuses.get(emp.id)?.hasProbationAgreement ?? false;

      if (isInProbationPeriod && !hasProbationAgreement) {
        // 수습기간 내 + 상호합의 미완료 → 2탭
        probation.push(emp);
      } else {
        // 상호합의 완료 / 수습기간 만료 / 비수습 → 1탭
        general.push(emp);
      }
    }
    return { generalEmployees: general, probationTabEmployees: probation };
  }, [sortedByInterview, today, interviewStatuses]);

  const activeTabEmployees = pageTab === 'general' ? generalEmployees : probationTabEmployees;

  // ============================================
  // 로딩 상태
  // ============================================
  const isLoading = scopeLoading || employeesLoading;
  const error = scopeError || employeesError;

  // ============================================
  // 에러 UI
  // ============================================
  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px',
          background: 'var(--color-red-50)',
          border: '1px solid var(--color-red-200)',
          borderRadius: '8px',
          color: 'var(--color-red-700)',
        }}>
          <AlertCircle size={20} />
          <div>
            <div style={{ fontWeight: 600 }}>데이터 조회 실패</div>
            <div style={{ fontSize: '14px', marginTop: '4px' }}>
              {error.message || '조직원 목록을 불러올 수 없습니다.'}
            </div>
          </div>
          <button
            onClick={() => {
              refetchScope();
              window.location.reload();
            }}
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              background: 'var(--color-red-100)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            <RefreshCw size={14} />
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // 로딩 UI
  // ============================================
  if (isLoading) {
    return (
      <div style={{ padding: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
          조직원 면담
        </h1>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          color: 'var(--color-gray-500)',
        }}>
          데이터 로딩 중...
        </div>
      </div>
    );
  }

  // ============================================
  // 메인 UI
  // ============================================
  return (
    <div style={{ padding: '24px' }}>
      {/* 헤더 */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold' }}>
            조직원 면담
          </h1>

          {/* 버튼 그룹 */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* V55.67: 면담관리 버튼 */}
            <button
              onClick={() => navigate('/org-management/interviews/manage')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                background: 'var(--color-gray-100)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                color: 'var(--color-gray-700)',
                fontWeight: 500,
              }}
            >
              <ClipboardList size={16} />
              면담관리
            </button>

            {/* V55.61: 면담주기 설정 버튼 */}
            <button
              onClick={() => setShowCycleSettingsModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                background: 'var(--color-gray-100)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                color: 'var(--color-gray-700)',
                fontWeight: 500,
              }}
            >
              <Settings size={16} />
              면담주기 설정
            </button>
          </div>
        </div>

        {/* 조직 정보 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: 'var(--color-gray-600)',
          fontSize: '14px',
        }}>
          <Users size={16} />
          <span style={{ fontWeight: 500 }}>
            {requester?.orgUnitName || '조직 정보 없음'}
          </span>
          {scopeMode && (
            <span style={{
              padding: '2px 8px',
              background: 'var(--color-blue-50)',
              color: 'var(--color-blue-700)',
              borderRadius: '4px',
              fontSize: '12px',
            }}>
              {SCOPE_MODE_LABELS[scopeMode] || scopeMode}
            </span>
          )}
          <span style={{ color: 'var(--color-gray-400)' }}>|</span>
          <span>
            대상 인원: {activeTabEmployees.length}명
          </span>
        </div>
      </div>

      {/* V62: 페이지 탭 (면담 / 수습면담) */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '16px',
        borderBottom: '2px solid var(--color-gray-200)',
      }}>
        <button
          onClick={() => setPageTab('general')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: 'transparent',
            borderBottom: pageTab === 'general' ? '2px solid #4f46e5' : '2px solid transparent',
            marginBottom: '-2px',
            color: pageTab === 'general' ? '#4f46e5' : 'var(--color-gray-500)',
            fontWeight: pageTab === 'general' ? 600 : 400,
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          면담
          <span style={{
            marginLeft: '6px',
            fontSize: '12px',
            color: pageTab === 'general' ? '#4f46e5' : 'var(--color-gray-400)',
          }}>
            {generalEmployees.length}
          </span>
        </button>
        <button
          onClick={() => setPageTab('probation')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: 'transparent',
            borderBottom: pageTab === 'probation' ? '2px solid #4f46e5' : '2px solid transparent',
            marginBottom: '-2px',
            color: pageTab === 'probation' ? '#4f46e5' : 'var(--color-gray-500)',
            fontWeight: pageTab === 'probation' ? 600 : 400,
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          수습면담
          <span style={{
            marginLeft: '6px',
            fontSize: '12px',
            color: pageTab === 'probation' ? '#4f46e5' : 'var(--color-gray-400)',
          }}>
            {probationTabEmployees.length}
          </span>
        </button>
      </div>

      {/* 검색 바 */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 14px',
          background: 'var(--color-gray-50)',
          border: '1px solid var(--color-gray-200)',
          borderRadius: '8px',
          maxWidth: '400px',
        }}>
          <Search size={18} style={{ color: 'var(--color-gray-400)' }} />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="이름, 소속, 직위, 직책으로 검색..."
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: '14px',
            }}
          />
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              style={{
                padding: '2px 6px',
                background: 'var(--color-gray-200)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                color: 'var(--color-gray-600)',
              }}
            >
              초기화
            </button>
          )}
        </div>
      </div>

      {/* 테이블 */}
      <div style={{
        background: 'white',
        border: '1px solid var(--color-gray-200)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-gray-50)' }}>
              <th style={thStyle}>이름</th>
              <th style={thStyle}>소속</th>
              <th style={thStyle}>직위</th>
              <th style={thStyle}>직책</th>
              <th style={thStyle}>고용형태</th>
              <th style={thStyle}>마지막 면담일</th>
              <th style={thStyle}>마지막 면담상태</th>
              <th style={thStyle}>면담관리 상태</th>
            </tr>
          </thead>
          <tbody>
            {activeTabEmployees.length === 0 ? (
              <tr>
                <td colSpan={8} style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: 'var(--color-gray-500)',
                }}>
                  {searchText ? '검색 결과가 없습니다.' : '조직원이 없습니다.'}
                </td>
              </tr>
            ) : (
              activeTabEmployees.map((emp) => (
                <EmployeeRow
                  key={emp.id}
                  employee={emp}
                  isSelected={selectedEmployee?.id === emp.id}
                  onClick={() => handleOpenPanel(emp)}
                  interviewStatus={interviewStatuses.get(emp.id)}
                  statusLoading={statusLoading}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 안내 메시지 */}
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        background: 'var(--color-gray-50)',
        borderRadius: '8px',
        fontSize: '13px',
        color: 'var(--color-gray-600)',
      }}>
직원 행을 클릭하면 면담 상세 패널이 열립니다.
      </div>

      {/* 상세 패널 */}
      {showDetailPanel && selectedEmployee && (
        <InterviewDetailPanel
          employee={selectedEmployee}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          interviewDate={interviewDate}
          setInterviewDate={setInterviewDate}
          interviewTime={interviewTime}
          setInterviewTime={setInterviewTime}
          interviewLocation={interviewLocation}
          setInterviewLocation={setInterviewLocation}
          interviewType={interviewType}
          setInterviewType={setInterviewType}
          onClose={handleClosePanel}
          onGoToHistory={handleGoToHistory}
          onGoToWritePage={handleGoToWritePage}
        />
      )}

      {/* V55.61: 면담주기 설정 모달 */}
      <InterviewCycleSettingsModal
        isOpen={showCycleSettingsModal}
        onClose={() => {
          setShowCycleSettingsModal(false);
          refetchStatuses(); // 설정 저장 후 상태 새로고침
        }}
      />
    </div>
  );
}

// ============================================
// 테이블 행 컴포넌트
// ============================================

interface EmployeeRowProps {
  employee: Employee;
  isSelected: boolean;
  onClick: () => void;
  interviewStatus?: EmployeeInterviewStatus;
  statusLoading?: boolean;
}

function EmployeeRow({ employee, isSelected, onClick, interviewStatus, statusLoading }: EmployeeRowProps) {
  // V55.61: 서버에서 계산된 면담관리 상태 사용 (면담주기 설정 기반)
  const statusData = interviewStatus?.status;
  const statusStyle = statusData
    ? CALCULATED_STATUS_STYLES[statusData.status]
    : CALCULATED_STATUS_STYLES.none;

  // 마지막 면담일
  const lastInterviewDate = interviewStatus?.lastInterviewDate
    ? interviewStatus.lastInterviewDate.replace(/-/g, '.')
    : '-';

  // V55.65: 마지막 면담상태 (가장 최근 면담 1건의 deliveryStatus)
  // - 라벨: DELIVERY_STATUS_LABELS (useInterviews.ts SSoT)
  // - 스타일: DELIVERY_STATUS_COLORS (파일 상단 정의)
  const lastDeliveryStatus = interviewStatus?.lastDeliveryStatus;
  const lastDeliveryLabel = lastDeliveryStatus ? DELIVERY_STATUS_LABELS[lastDeliveryStatus] : null;
  const lastDeliveryStyle = lastDeliveryStatus ? DELIVERY_STATUS_COLORS[lastDeliveryStatus] : null;

  return (
    <tr
      onClick={onClick}
      style={{
        borderTop: '1px solid var(--color-gray-100)',
        background: isSelected ? 'var(--color-blue-50)' : undefined,
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.background = 'var(--color-gray-50)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isSelected ? 'var(--color-blue-50)' : '';
      }}
    >
      {/* 이름 */}
      <td style={tdStyle}>
        <span style={{ fontWeight: 500, color: 'var(--color-gray-900)', fontSize: '14px' }}>
          {employee.nameKo || '-'}
        </span>
      </td>

      {/* 소속 */}
      <td style={tdStyle}>
        {employee.orgUnitName || '-'}
      </td>

      {/* 직위 */}
      <td style={tdStyle}>
        {employee.positionTitle || '-'}
      </td>

      {/* 직책 */}
      <td style={tdStyle}>
        {employee.jobTitle || '-'}
      </td>

      {/* 고용형태 */}
      <td style={tdStyle}>
        {employee.currentEmploymentType || '-'}
      </td>

      {/* 마지막 면담일 */}
      <td style={tdStyle}>
        {lastInterviewDate}
      </td>

      {/* 마지막 면담상태 (가장 최근 면담 1건의 deliveryStatus) */}
      <td style={tdStyle}>
        {lastDeliveryLabel && lastDeliveryStyle ? (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
            color: lastDeliveryStyle.color,
            background: lastDeliveryStyle.bg,
          }}>
            {lastDeliveryLabel}
          </span>
        ) : (
          <span style={{ color: 'var(--color-gray-400)' }}>-</span>
        )}
      </td>

      {/* 면담관리 상태 (면담주기 설정 기반 계산 결과) */}
      <td style={tdStyle}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 500,
          color: statusStyle.color,
          background: statusStyle.bg,
        }}>
          {/* 수습면담 임박 표시 */}
          {statusData?.isProbationAlert && (
            <span style={{ fontSize: '10px' }}>⚠️</span>
          )}
          {/* 면담관리 상태 라벨 + D-day 텍스트 */}
          {statusData?.label || statusStyle.label}
          {statusData?.dDayText && (
            <span style={{ fontSize: '11px', opacity: 0.9 }}>
              {statusData.dDayText}
            </span>
          )}
        </span>
      </td>
    </tr>
  );
}

// ============================================
// 면담 상세 패널 컴포넌트
// ============================================

interface InterviewDetailPanelProps {
  employee: Employee;
  activeTab: PanelTab;
  setActiveTab: (tab: PanelTab) => void;
  // 날짜/시간 분리
  interviewDate: string;
  setInterviewDate: (value: string) => void;
  interviewTime: string;
  setInterviewTime: (value: string) => void;
  interviewLocation: string;
  setInterviewLocation: (value: string) => void;
  interviewType: InterviewType;
  setInterviewType: (value: InterviewType) => void;
  onClose: () => void;
  onGoToHistory: () => void;
  // 면담 기록 작성 페이지로 이동
  onGoToWritePage: () => void;
}

function InterviewDetailPanel({
  employee,
  activeTab,
  setActiveTab,
  interviewDate,
  setInterviewDate,
  interviewTime,
  setInterviewTime,
  interviewLocation,
  setInterviewLocation,
  interviewType,
  setInterviewType,
  onClose,
  onGoToHistory,
  onGoToWritePage,
}: InterviewDetailPanelProps) {
  return (
    <>
      {/* Backdrop */}
      <ModalOverlay
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 998,
        }}
        onClose={onClose}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: '480px',
          background: 'white',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            조직원 면담 상세
          </h2>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 직원 기본 정보 */}
        <div
          style={{
            padding: '16px 20px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
            {employee.nameKo || '-'}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
            fontSize: '13px',
            color: '#64748b',
          }}>
            <div>
              <span style={{ color: '#94a3b8' }}>소속:</span> {employee.orgUnitName || '-'}
            </div>
            <div>
              <span style={{ color: '#94a3b8' }}>직위:</span> {employee.positionTitle || '-'}
            </div>
            <div>
              <span style={{ color: '#94a3b8' }}>직책:</span> {employee.jobTitle || '-'}
            </div>
            <div>
              <span style={{ color: '#94a3b8' }}>고용형태:</span> {employee.currentEmploymentType || '-'}
            </div>
          </div>
        </div>

        {/* 탭 바 */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <button
            onClick={() => setActiveTab('interview')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              background: activeTab === 'interview' ? 'white' : '#f8fafc',
              borderBottom: activeTab === 'interview' ? '2px solid #4f46e5' : '2px solid transparent',
              color: activeTab === 'interview' ? '#4f46e5' : '#64748b',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              fontSize: '14px',
            }}
          >
            <ClipboardList size={16} />
            면담진행
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              background: activeTab === 'history' ? 'white' : '#f8fafc',
              borderBottom: activeTab === 'history' ? '2px solid #4f46e5' : '2px solid transparent',
              color: activeTab === 'history' ? '#4f46e5' : '#64748b',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              fontSize: '14px',
            }}
          >
            <FileText size={16} />
            면담기록
          </button>
        </div>

        {/* 탭 콘텐츠 */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {activeTab === 'interview' ? (
            <InterviewFormTab
              interviewDate={interviewDate}
              setInterviewDate={setInterviewDate}
              interviewTime={interviewTime}
              setInterviewTime={setInterviewTime}
              interviewLocation={interviewLocation}
              setInterviewLocation={setInterviewLocation}
              interviewType={interviewType}
              setInterviewType={setInterviewType}
              onGoToHistory={onGoToHistory}
              onGoToWritePage={onGoToWritePage}
            />
          ) : (
            <InterviewHistoryTab
              targetUserId={employee.id}
              employeeName={employee.nameKo}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ============================================
// 면담진행 탭 (탭1)
// ============================================

// 날짜/시간 분리
// 면담 기록 작성 페이지 이동 핸들러 추가
interface InterviewFormTabProps {
  interviewDate: string;
  setInterviewDate: (value: string) => void;
  interviewTime: string;
  setInterviewTime: (value: string) => void;
  interviewLocation: string;
  setInterviewLocation: (value: string) => void;
  interviewType: InterviewType;
  setInterviewType: (value: InterviewType) => void;
  onGoToHistory: () => void;
  onGoToWritePage: () => void;
}

function InterviewFormTab({
  interviewDate,
  setInterviewDate,
  interviewTime,
  setInterviewTime,
  interviewLocation,
  setInterviewLocation,
  interviewType,
  setInterviewType,
  onGoToHistory,
  onGoToWritePage,
}: InterviewFormTabProps) {
  // V60.x: BE에서 활성 면담 타입만 조회 (비활성 타입 미노출, fallback 금지)
  const { types: activeTypes, isLoading: typesLoading, error: typesError } = useInterviewTypes();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* 면담일시 - 날짜/시간 분리 (같은 줄 flex row) */}
      <div>
        <label style={labelStyle}>
          면담일시
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="date"
            value={interviewDate}
            onChange={(e) => setInterviewDate(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          />
          <input
            type="time"
            value={interviewTime}
            onChange={(e) => setInterviewTime(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          />
        </div>
      </div>

      {/* 면담장소 */}
      <div>
        <label style={labelStyle}>
          면담장소
        </label>
        <input
          type="text"
          value={interviewLocation}
          onChange={(e) => setInterviewLocation(e.target.value)}
          placeholder="예: 5번 회의실"
          style={inputStyle}
        />
      </div>

      {/* 면담타입 — V60.x: BE 활성 타입만 표시, 하드코딩 fallback 금지 */}
      <div>
        <label style={labelStyle}>
          면담타입
        </label>
        {typesLoading ? (
          <select disabled style={{ ...inputStyle, color: '#94a3b8' }}>
            <option>면담 타입 불러오는 중...</option>
          </select>
        ) : typesError ? (
          <div style={{ padding: 8, background: '#FEF2F2', borderRadius: 4, color: '#DC2626', fontSize: 12 }}>
            면담 타입을 불러오지 못했습니다. 새로고침 해주세요.
          </div>
        ) : (
          <select
            value={interviewType}
            onChange={(e) => setInterviewType(e.target.value as InterviewType)}
            style={inputStyle}
          >
            {activeTypes.map((t) => (
              <option key={t.code} value={t.code}>
                {t.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* 면담 기록 작성 버튼 - 별도 URL로 이동 */}
      <div style={{ marginTop: '12px' }}>
        <button
          onClick={onGoToWritePage}
          style={{
            width: '100%',
            padding: '12px 16px',
            background: '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          면담 기록 작성
        </button>
        <p style={{
          marginTop: '8px',
          fontSize: '12px',
          color: '#94a3b8',
          textAlign: 'center',
        }}>
          * 버튼 클릭 시 면담 기록 작성 화면으로 이동합니다.
        </p>
      </div>
    </div>
  );
}

// ============================================
// 면담기록 탭 (탭2) - 면담 클릭 시 InterviewWritePage로 이동
// ============================================

interface InterviewHistoryTabProps {
  targetUserId: string;
  employeeName?: string;
}

function InterviewHistoryTab({ targetUserId, employeeName }: InterviewHistoryTabProps) {
  const navigate = useNavigate();
  const { interviews, isLoading, error, refetch } = useInterviews(targetUserId);

  // 면담 클릭 → InterviewWritePage로 이동
  const handleInterviewClick = useCallback((interview: Interview) => {
    const params = new URLSearchParams({
      interviewId: interview.id,
    });
    navigate(`/org-management/interviews/${interview.targetUserId}/new?${params.toString()}`);
  }, [navigate]);

  // 로딩 중
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          color: '#64748b',
        }}
      >
        <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '8px' }}>로딩 중...</span>
      </div>
    );
  }

  // 에러
  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <AlertCircle size={32} style={{ color: '#ef4444', marginBottom: '12px' }} />
        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>
          면담 기록을 불러오는데 실패했습니다.
        </div>
        <button
          onClick={refetch}
          style={{
            padding: '6px 12px',
            background: '#f1f5f9',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          다시 시도
        </button>
      </div>
    );
  }

  // 빈 상태
  if (interviews.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          padding: '40px 20px',
          textAlign: 'center',
        }}
      >
        <FileText
          size={48}
          style={{ color: '#cbd5e1', marginBottom: '16px' }}
        />
        <div style={{ fontSize: '15px', color: '#64748b', marginBottom: '8px' }}>
          해당 직원의 면담기록이 없습니다.
        </div>
        <div style={{ fontSize: '13px', color: '#94a3b8' }}>
          {employeeName ? `${employeeName}님의 ` : ''}면담 기록 작성 시 이곳에 표시됩니다.
        </div>
      </div>
    );
  }

  // 면담 목록 표시
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {interviews.map((interview) => (
        <InterviewHistoryItem
          key={interview.id}
          interview={interview}
          onClick={() => handleInterviewClick(interview)}
        />
      ))}
    </div>
  );
}

// ============================================
// 면담기록 아이템 컴포넌트
// ============================================

interface InterviewHistoryItemProps {
  interview: Interview;
  onClick?: () => void;
}

function InterviewHistoryItem({ interview, onClick }: InterviewHistoryItemProps) {
  const dateStr = interview.interviewDate?.replace(/-/g, '.') || '-';
  const timeStr = interview.interviewTime || '';
  const formattedDate = timeStr ? `${dateStr} ${timeStr}` : dateStr;
  // V62: typeLabel(BE resolveTypeLabel → DB name 스냅샷) 전용 — "면담 목적 = 면담 타입명" 정책
  // 하드코딩 INTERVIEW_TYPE_LABELS, '기타' fallback 제거
  const typeLabel = interview.typeLabel || '-';

  // 마지막 면담상태 - UI 표시용 (BE 도메인 값과 동기화 필요)
  const deliveryStatus = interview.deliveryStatus || 'notSent';
  const deliveryLabel = DELIVERY_STATUS_LABELS[deliveryStatus] ?? DELIVERY_STATUS_LABELS.notSent;
  const deliveryStyle = DELIVERY_STATUS_COLORS[deliveryStatus] ?? DELIVERY_STATUS_COLORS.notSent;

  return (
    <div
      onClick={onClick}
      style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '14px 16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.2s, border-color 0.2s',
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.background = '#f1f5f9';
          e.currentTarget.style.borderColor = '#cbd5e1';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#f8fafc';
        e.currentTarget.style.borderColor = '#e2e8f0';
      }}
    >
      {/* 상단: 날짜 + 상태 배지들 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <span style={{ fontSize: '13px', color: '#64748b' }}>
          {formattedDate}
        </span>
        <div style={{ display: 'flex', gap: '6px' }}>
          {/* 저장 상태 배지 제거 - deliveryStatus만 표시 */}
          <span
            style={{
              fontSize: '11px',
              fontWeight: 500,
              padding: '2px 8px',
              borderRadius: '4px',
              color: deliveryStyle.color,
              background: deliveryStyle.bg,
            }}
          >
            {deliveryLabel}
          </span>
        </div>
      </div>

      {/* 면담 타입 */}
      <div style={{ fontSize: '14px', fontWeight: 500, color: '#334155', marginBottom: '4px' }}>
        {typeLabel}
      </div>

      {/* 면담자 */}
      <div style={{ fontSize: '12px', color: '#94a3b8' }}>
        면담자: {interview.interviewerName || '-'}
      </div>
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'center',
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-gray-700)',
  borderBottom: '1px solid var(--color-gray-200)',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'center',
  fontSize: '14px',
  color: 'var(--color-gray-800)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};
